import { useStore } from 'jotai';
import React from 'react';
import { useFileViewerContext } from 'js/file_viewer/contexts/file_viewer';
import { filePreviewSessionIdAtom } from 'js/file_viewer/core/atoms/ui/logging';
import { useFileViewerAtomValue } from 'js/file_viewer/core/atoms/use_atom';
import { EventType } from 'js/file_viewer/core/logging/constants';
import { serializeSession, getLoggingSession } from 'js/file_viewer/core/logging/session';
import { VideoPreviewEvent } from 'js/file_viewer/core/logging/video';
import { ErrorLifecycle, FVError, FVErrorCode } from 'js/file_viewer/core/types/errors';
import { PreviewType } from 'js/file_viewer/core/types/preview_type';
import { useStabilizedCallback } from 'js/file_viewer/core/utils/useStabilizedCallback';
/**
 * If a logger is called before the session is initialized, we store the event
 * data until the session is ready and then we log.
 */
const queuedPreviewEvents = [];
const useLogPreviewEventUnconnected = (store, logger) => {
    return useStabilizedCallback((event, extra) => {
        const filePreviewSessionId = store.get(filePreviewSessionIdAtom);
        const session = getLoggingSession(filePreviewSessionId);
        if (!session) {
            queuedPreviewEvents.push([event, extra]);
            return;
        }
        logger === null || logger === void 0 ? void 0 : logger.log(event, serializeSession({
            ...session,
            extra: {
                source_context: session.extra['source_context'],
                preview_type: session.extra['preview_type'],
                ...extra,
            },
        }));
    });
};
export const useLogPreviewEvent = () => {
    const { logger } = useFileViewerContext();
    const store = useStore();
    return useLogPreviewEventUnconnected(store, logger);
};
/**
 * Returns a callback that generically logs things the same way as internal FileViewer logging.
 * This includes all preview session information for free.
 * The log is tagged as `EventType.UserAction` in preview tables. The other args all end up in the `extra` map.
 *
 * This is meant for internal use to set up FileViewerContext. If you are writing a File Viewer feature, log to the
 * copy of the returned callback stored in FileViewerContext.
 *
 * @param action: a string describing the action
 * @param context: a string describing where the action occurred, if relevant
 * @param extra: any other extra information that you want to have in the logged event.
 */
export const useLogUserActionInternal = (store, logger) => {
    const logPreviewEvent = useLogPreviewEventUnconnected(store, logger);
    return useStabilizedCallback((action, context, extra) => {
        logPreviewEvent(EventType.UserAction, {
            action,
            ...(context ? { context } : {}),
            ...extra,
        });
    });
};
export const useLogBackgroundEvent = () => {
    const logPreviewEvent = useLogPreviewEvent();
    return useStabilizedCallback((event, extra) => {
        logPreviewEvent(EventType.Background, {
            ...extra,
            event,
        });
    });
};
export const usePurgeQueuedEvents = () => {
    const previewSessionId = useFileViewerAtomValue(filePreviewSessionIdAtom);
    const logPreviewEvent = useLogPreviewEvent();
    React.useEffect(() => {
        if (!previewSessionId) {
            return;
        }
        while (queuedPreviewEvents.length) {
            const event = queuedPreviewEvents.pop();
            if (event) {
                logPreviewEvent(...event);
            }
        }
    }, [logPreviewEvent, previewSessionId]);
};
const getVideoSession = (filePreviewSessionId) => {
    const session = getLoggingSession(filePreviewSessionId);
    if (!session) {
        return undefined;
    }
    const { 
    /* eslint-disable @typescript-eslint/naming-convention */
    file_preview_session_id, file_viewer_session_id, ns_id, sj_id, device_type, file_id, extra: { preview_type, source_context },
    /* eslint-enable @typescript-eslint/naming-convention */
     } = session !== null && session !== void 0 ? session : { extra: {} };
    return file_preview_session_id &&
        file_viewer_session_id &&
        preview_type &&
        source_context &&
        device_type
        ? {
            file_preview_session_id,
            file_viewer_session_id,
            ns_id,
            sj_id,
            device_type,
            preview_type,
            source_context,
            file_id,
        }
        : undefined;
};
const serializeVideoLogOpts = (payload, videoPreviewSession, previewType) => {
    const { videoFormat, videoDuration, videoTruncated, extra } = payload;
    return {
        file_viewer_session_id: videoPreviewSession.file_viewer_session_id,
        file_preview_session_id: videoPreviewSession.file_preview_session_id,
        video_format: videoFormat,
        video_duration_seconds: videoDuration,
        video_truncated: videoTruncated,
        source_context: videoPreviewSession.source_context,
        device_type: videoPreviewSession.device_type,
        preview_type: previewType,
        file_id: videoPreviewSession.file_id,
        extra,
    };
};
const excludedDurationEventList = [
    VideoPreviewEvent.PlayerRendered, // VideoJs maybe initialized before video metadata is ready
    VideoPreviewEvent.FirstClickPlay, // User clicking play before the video metadata is ready
    VideoPreviewEvent.MediaError, // media error can be fired before loadedmetadata is ready
];
const getVideoDuration = (videoPlayer, event) => {
    if (videoPlayer && !excludedDurationEventList.includes(event)) {
        return videoPlayer.duration() || undefined;
    }
    return undefined;
};
const getLoggingArgs = (filePreviewSessionId, eventName, videoTruncated, player, extra) => {
    const videoSession = getVideoSession(filePreviewSessionId);
    if (!filePreviewSessionId || !videoSession) {
        return;
    }
    const previewType = videoSession.preview_type;
    if (previewType !== PreviewType.Video && previewType !== PreviewType.Audio) {
        throw new FVError(FVErrorCode.InvalidLoggingSession, ErrorLifecycle.Unknown, "Can't log video events for non-audio/video previews.");
    }
    const args = {
        filePreviewSessionId,
        videoTruncated,
        videoDuration: getVideoDuration(player, eventName),
        extra,
    };
    return { args, previewType, videoSession };
};
export const useLogVideoEvent = (videoTruncated, player) => {
    const { logger } = useFileViewerContext();
    const filePreviewSessionId = useFileViewerAtomValue(filePreviewSessionIdAtom);
    return useStabilizedCallback((eventName, extra) => {
        var _a;
        const loggingArgs = getLoggingArgs(filePreviewSessionId, eventName, videoTruncated, player, extra);
        if (!loggingArgs) {
            return;
        }
        const { args, previewType, videoSession } = loggingArgs;
        (_a = logger === null || logger === void 0 ? void 0 : logger.logVideoEvent) === null || _a === void 0 ? void 0 : _a.call(logger, eventName, serializeVideoLogOpts(args, videoSession, previewType));
    });
};
export const useLogVideoEventQuantity = (videoTruncated, player) => {
    const { logger } = useFileViewerContext();
    const filePreviewSessionId = useFileViewerAtomValue(filePreviewSessionIdAtom);
    return useStabilizedCallback((eventName, quantity, extra) => {
        var _a;
        const loggingArgs = getLoggingArgs(filePreviewSessionId, eventName, videoTruncated, player, extra);
        if (!loggingArgs) {
            return;
        }
        const { args, previewType, videoSession } = loggingArgs;
        (_a = logger === null || logger === void 0 ? void 0 : logger.logVideoEventQuantity) === null || _a === void 0 ? void 0 : _a.call(logger, eventName, quantity, serializeVideoLogOpts(args, videoSession, previewType));
    });
};
export const useLogVideoEventDuration = (videoTruncated, player) => {
    const { logger } = useFileViewerContext();
    const filePreviewSessionId = useFileViewerAtomValue(filePreviewSessionIdAtom);
    return useStabilizedCallback((eventName, durationInMs, extra) => {
        var _a;
        const loggingArgs = getLoggingArgs(filePreviewSessionId, eventName, videoTruncated, player, extra);
        if (!loggingArgs) {
            return;
        }
        const { args, previewType, videoSession } = loggingArgs;
        (_a = logger === null || logger === void 0 ? void 0 : logger.logVideoEventDuration) === null || _a === void 0 ? void 0 : _a.call(logger, eventName, durationInMs, serializeVideoLogOpts(args, videoSession, previewType));
    });
};
//# sourceMappingURL=logging.preenvesnextjs.map
